/**
 * PR77: Pake Desktop Shell Build Plan Registry — Registry
 *
 * Metadata-only build plans for Smart Shikka desktop app targets.
 * No Pake source code is copied.
 * No Pake command is executed.
 * No shell command is run.
 * No installer is created.
 * No user drive read/write happens here.
 *
 * Deep-frozen — every target object, platform array, blocked action list,
 * required approval list, and notes array is recursively frozen so that
 * runtime mutation is impossible.
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// ---------------------------------------------------------------------------
function deepFreeze(value) {
    if (value === null || typeof value !== "object")
        return value;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        const child = value[key];
        if (child !== null && typeof child === "object" && !Object.isFrozen(child)) {
            deepFreeze(child);
        }
    }
    return value;
}
// ---------------------------------------------------------------------------
// Shared safety constants
// ---------------------------------------------------------------------------
const COMMON_BLOCKED_ACTIONS = deepFreeze([
    "copy_pake_source",
    "vendor_pake_repo",
    "run_pake_command",
    "run_shell_command",
    "create_installer",
    "write_user_drive",
    "read_user_drive",
    "open_browser",
    "control_desktop",
    "network_call",
    "secret_read",
    "db_write",
    "supabase_call",
    "provider_call",
    "auto_save",
    "auto_delete",
    "payment_trigger",
    "role_mutation",
    "secret_mutation",
]);
const COMMON_PLATFORMS = deepFreeze([
    "windows",
    "macos",
    "linux",
]);
const COMMON_REQUIRED_APPROVALS = deepFreeze([
    "owner_license_review",
    "desktop_build_plan_approval",
    "no_source_copy_confirmation",
]);
// ---------------------------------------------------------------------------
// Desktop target definitions
// ---------------------------------------------------------------------------
const SMART_PRINT_DESKTOP = deepFreeze({
    id: "smart_print_desktop",
    displayName: "Smart Print Desktop Shell",
    appKey: "smart-print",
    sourceAppType: "smart_print_calculator",
    defaultUrlPlaceholder: "https://print.localhost:5173/",
    width: 1200,
    height: 800,
    resizable: true,
    fullscreen: false,
    platforms: COMMON_PLATFORMS,
    iconPolicy: "placeholder_required",
    dataStorageMode: "planned_local_drive",
    localDataAllowed: false,
    cloudSyncAllowed: false,
    manualBuildOnly: true,
    pakeSourceCopyAllowed: false,
    pakeExecutionAllowed: false,
    networkDuringBuildAllowed: false,
    secretAccessAllowed: false,
    fileMutationAllowed: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    requiredApprovals: COMMON_REQUIRED_APPROVALS,
    buildCommandPreview: 'pnpm exec pake "https://print.localhost:5173/" --name "Smart Print" --width 1200 --height 800',
    notes: [
        "Local-drive / Tally-style data mode is planned for a later PR.",
        "Do not add SQLite, backup, restore, import/export, or user drive access in PR77.",
        "PR77 is build-plan metadata only.",
    ],
});
const SMART_SALARY_DESKTOP = deepFreeze({
    id: "smart_salary_desktop",
    displayName: "Smart Salary Desktop Shell",
    appKey: "smart-salary",
    sourceAppType: "smart_salary_app",
    defaultUrlPlaceholder: "https://salary.localhost:5173/",
    width: 1200,
    height: 800,
    resizable: true,
    fullscreen: false,
    platforms: COMMON_PLATFORMS,
    iconPolicy: "placeholder_required",
    dataStorageMode: "metadata_only",
    localDataAllowed: false,
    cloudSyncAllowed: false,
    manualBuildOnly: true,
    pakeSourceCopyAllowed: false,
    pakeExecutionAllowed: false,
    networkDuringBuildAllowed: false,
    secretAccessAllowed: false,
    fileMutationAllowed: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    requiredApprovals: COMMON_REQUIRED_APPROVALS,
    buildCommandPreview: 'pnpm exec pake "https://salary.localhost:5173/" --name "Smart Salary" --width 1200 --height 800',
    notes: [
        "Salary app runs as a wrapped web shell in PR77.",
        "No local database or file storage is implemented in this PR.",
    ],
});
const DRISHTI_KUNDALI_DESKTOP = deepFreeze({
    id: "drishti_kundali_desktop",
    displayName: "Drishti Kundali Desktop Shell",
    appKey: "drishti-kundali",
    sourceAppType: "drishti_kundali_matching",
    defaultUrlPlaceholder: "https://drishti.localhost:5173/",
    width: 1280,
    height: 900,
    resizable: true,
    fullscreen: false,
    platforms: COMMON_PLATFORMS,
    iconPolicy: "placeholder_required",
    dataStorageMode: "metadata_only",
    localDataAllowed: false,
    cloudSyncAllowed: false,
    manualBuildOnly: true,
    pakeSourceCopyAllowed: false,
    pakeExecutionAllowed: false,
    networkDuringBuildAllowed: false,
    secretAccessAllowed: false,
    fileMutationAllowed: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    requiredApprovals: COMMON_REQUIRED_APPROVALS,
    buildCommandPreview: 'pnpm exec pake "https://drishti.localhost:5173/" --name "Drishti Kundali" --width 1280 --height 900',
    notes: [
        "Kundali matching UI can be wrapped as a desktop shell.",
        "No local chart storage or PDF export in PR77.",
    ],
});
const DIGITAL_ANOOPAM_DESKTOP = deepFreeze({
    id: "digital_anoopam_desktop",
    displayName: "Digital Anoopam Press Desktop Shell",
    appKey: "digital-anoopam",
    sourceAppType: "digital_anoopam_press_app_hub",
    defaultUrlPlaceholder: "https://anoopam.localhost:5173/",
    width: 1400,
    height: 900,
    resizable: true,
    fullscreen: false,
    platforms: COMMON_PLATFORMS,
    iconPolicy: "placeholder_required",
    dataStorageMode: "metadata_only",
    localDataAllowed: false,
    cloudSyncAllowed: false,
    manualBuildOnly: true,
    pakeSourceCopyAllowed: false,
    pakeExecutionAllowed: false,
    networkDuringBuildAllowed: false,
    secretAccessAllowed: false,
    fileMutationAllowed: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    requiredApprovals: COMMON_REQUIRED_APPROVALS,
    buildCommandPreview: 'pnpm exec pake "https://anoopam.localhost:5173/" --name "Digital Anoopam" --width 1400 --height 900',
    notes: [
        "Press app hub can be wrapped as a desktop shell.",
        "No local file publishing or media download in PR77.",
    ],
});
const SMART_SHIKKA_CONTROL_CENTER_DESKTOP = deepFreeze({
    id: "smart_shikka_control_center_desktop",
    displayName: "Smart Shikka Control Center Desktop Shell",
    appKey: "smart-shikka-control-center",
    sourceAppType: "smart_shikka_control_center",
    defaultUrlPlaceholder: "https://control.localhost:5173/",
    width: 1440,
    height: 900,
    resizable: true,
    fullscreen: false,
    platforms: COMMON_PLATFORMS,
    iconPolicy: "owner_approved_icon_required",
    dataStorageMode: "metadata_only",
    localDataAllowed: false,
    cloudSyncAllowed: false,
    manualBuildOnly: true,
    pakeSourceCopyAllowed: false,
    pakeExecutionAllowed: false,
    networkDuringBuildAllowed: false,
    secretAccessAllowed: false,
    fileMutationAllowed: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    requiredApprovals: deepFreeze([
        ...COMMON_REQUIRED_APPROVALS,
        "owner_icon_approval",
    ]),
    buildCommandPreview: 'pnpm exec pake "https://control.localhost:5173/" --name "Smart Shikka Control Center" --width 1440 --height 900',
    notes: [
        "Owner-approved icon is required before any real build.",
        "This is a build-plan entry only; no actual control-center desktop build is produced in PR77.",
    ],
});
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const PAKE_DESKTOP_BUILD_TARGETS = deepFreeze([
    SMART_PRINT_DESKTOP,
    SMART_SALARY_DESKTOP,
    DRISHTI_KUNDALI_DESKTOP,
    DIGITAL_ANOOPAM_DESKTOP,
    SMART_SHIKKA_CONTROL_CENTER_DESKTOP,
]);
export const PAKE_DESKTOP_BUILD_PLAN_REGISTRY = deepFreeze({
    version: "1.0.0",
    generatedInPr: 77,
    licenseBoundary: "pake_gpl_3_0_or_later_no_source_copy",
    targets: PAKE_DESKTOP_BUILD_TARGETS,
});
// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
export function getPakeDesktopBuildTargets() {
    return PAKE_DESKTOP_BUILD_TARGETS;
}
export function getPakeDesktopBuildTargetById(id) {
    return PAKE_DESKTOP_BUILD_TARGETS.find((t) => t.id === id);
}
export function getAllPakeDesktopBuildTargetIds() {
    return PAKE_DESKTOP_BUILD_TARGETS.map((t) => t.id);
}
export function getPakeDesktopBuildTargetsByPlatform(platform) {
    return PAKE_DESKTOP_BUILD_TARGETS.filter((t) => t.platforms.includes(platform));
}
export function getPakeDesktopBuildTargetsRequiringOwnerIcon() {
    return PAKE_DESKTOP_BUILD_TARGETS.filter((t) => t.iconPolicy === "owner_approved_icon_required");
}
//# sourceMappingURL=pakeDesktopBuildPlanRegistry.js.map